import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ApiErrorCode } from '../constants/errors.const';

const ApiErrorSchema = z.object({
  title: z.string(),
  status: z.number(),
  message: z.enum(ApiErrorCode),
  innerException: z.string(),
  errors: z.array(z.string()),
});

// Type for API error notification data-test-id (kebab-case format)
export type ApiErrorNotificationTestId = `api-error-${Lowercase<ApiErrorCode>}`;

// Generate data-test-id from error code (convert to lowercase and add prefix)
const generateApiErrorTestId = (
  errorCode: ApiErrorCode
): ApiErrorNotificationTestId => {
  return `api-error-${errorCode.toLowerCase() as Lowercase<ApiErrorCode>}`;
};

/**
 * Hook for showing backend error notifications with i18n translations
 *
 * @returns Object with functions to show different types of error notifications
 *
 * @example
 * ```ts
 * const { showApiErrorNotification, showCriticalApiErrorNotification } = useBackendErrorNotification();
 *
 * // Show standard error notification with translation
 * showApiErrorNotification(ApiErrorCode.ERROR_NOT_ENOUGH_MONEY);
 *
 * // Show critical error with custom message
 * showCriticalApiErrorNotification(
 *   ApiErrorCode.ERROR_CUSTOMER_BLOCKED,
 *   "Your account has been temporarily suspended."
 * );
 * ```
 */
export const useBackendErrorNotification = () => {
  const { t } = useTranslation();

  /**
   * Shows a standardized error notification for API backend errors using i18n translations
   *
   * @param errorCode - The API error code from the backend response
   * @param customMessage - Optional custom message to override the translated message
   */
  const showApiErrorNotification = (
    errorCode: ApiErrorCode,
    customMessage?: string
  ) => {
    const message = customMessage || t(`apiErrorCodes.${errorCode}`);
    const dataTestId = generateApiErrorTestId(errorCode);

    notifications.show({
      title: t('notifications.errorTitle'),
      message,
      color: 'red',
      autoClose: 5000,
      'data-test-id': dataTestId,
    });
  };

  /**
   * Shows a critical API error notification with extended display time
   * Use for errors that require immediate user attention
   *
   * @param errorCode - The API error code from the backend response
   * @param customMessage - Optional custom message to override the translated message
   */
  const showCriticalApiErrorNotification = (
    errorCode: ApiErrorCode,
    customMessage?: string
  ) => {
    const message = customMessage || t(`apiErrorCodes.${errorCode}`);
    const dataTestId = generateApiErrorTestId(errorCode);

    notifications.show({
      title: t('notifications.errorTitle'),
      message,
      color: 'red',
      autoClose: false, // Don't auto-close critical errors
      'data-test-id': dataTestId,
    });
  };

  /**
   * Get the translated message for an API error code without showing notification
   *
   * @param errorCode - The API error code
   * @returns The translated error message
   */
  const getApiErrorMessage = (errorCode: ApiErrorCode): string => {
    return t(`apiErrorCodes.${errorCode}`);
  };

  /**
   * Check if an error code is considered critical (requires immediate attention)
   *
   * @param errorCode - The API error code to check
   * @returns True if the error is critical
   */
  const isCriticalApiError = (errorCode: ApiErrorCode): boolean => {
    const criticalErrors: ApiErrorCode[] = [
      ApiErrorCode.ERROR_CUSTOMER_BLOCKED,
      ApiErrorCode.ERROR_PLAYER_LOCKED,
      ApiErrorCode.ERROR_IP_BLOCKED,
      ApiErrorCode.ERROR_COUNTRY_BLOCKED,
      ApiErrorCode.ERROR_SESSION_NOT_EXISTS,
      ApiErrorCode.ERROR_UPDATE_SESSION_SESSION_NOT_EXISTS,
    ];

    return criticalErrors.includes(errorCode);
  };

  /**
   * Show an error notification automatically choosing between standard and critical based on error type
   *
   * @param errorCode - The API error code from the backend response
   * @param customMessage - Optional custom message to override the translated message
   */
  const showAutoApiErrorNotification = (
    apiError: Error,
    customMessage?: string
  ) => {
    const parsedError = ApiErrorSchema.safeParse(apiError);

    if (!parsedError.success) {
      showApiErrorNotification(ApiErrorCode.ERROR_UNKNOWN, customMessage);
      return;
    }

    const errorCode = parsedError.data.message;

    const isErrorCodeInEnum = Object.values(ApiErrorCode).includes(errorCode);

    if (isErrorCodeInEnum) {
      if (isCriticalApiError(errorCode)) {
        showCriticalApiErrorNotification(errorCode, customMessage);
      } else {
        showApiErrorNotification(errorCode, customMessage);
      }
    } else {
      showApiErrorNotification(ApiErrorCode.ERROR_UNKNOWN, customMessage);
    }
  };

  return {
    showBackendErrorNotification: showAutoApiErrorNotification,
    getApiErrorMessage,
  };
};
